import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Prompt } from '@models/prompt.model';
import { Observable, distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs';
import { BaseComponent } from '../../../base.component';
import { ThirdPartySettings } from '@store/third-party/third-party.state';
import { Store } from '@ngrx/store';
import { currentOpenAiSettings } from '@store/third-party/open-ai-settings/open-ai-settings.selector';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { UserConfigsService } from '@services/user-configs.service';
import { AiAssistantStatus, AiAssistantStore, AiEngineModels } from '@store/component/ai-assistant.store';
import { currentPrompts } from '@store/ai-insights/prompts/prompts.selector';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ThirdPartyId } from '@models/third-party-id.enum';

@Component({
    selector: 'lex-ai-single-doc-prompt',
    templateUrl: './doc-viewer-co-pilot.component.html',
    styleUrls: ['./doc-viewer-co-pilot.component.scss'],
    animations: [
        trigger('fadeInOut', [
            state('title', style({ opacity: 0, width: '0px' })),
            state('fullPrompt', style({ opacity: 1, width: '100%' })),
            transition('title <=> fullPrompt', animate('400ms ease-in-out')),
        ])
    ],
    standalone: false
})
export class DocViewerCoPilotComponent extends BaseComponent implements OnInit, OnDestroy {

    @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
    @ViewChild('scrollResult') private scrollContainer: ElementRef;

    public status$: Observable<AiAssistantStatus> = this.aiAssistantStore.currentStatus$;
    public response$: Observable<string[]> = this.aiAssistantStore.currentResponse$;

    public localPrompts: Prompt[];

    public isLoading: boolean = false;
    public result: string[] = [];

    public showContinue: boolean = false;
    public showRetry: boolean = false;

    public showPromptControl: boolean = true;

    public selectedPrompt: Prompt = null;
    public promptControl = new UntypedFormControl('');
    public hoverPromptId: string = '';

    public showTokenWarning: boolean = false;
    public showTokenError: boolean = false;
    public currentUserId = '';

    public mode: string = 'indeterminate';

    private currentDocId: string = '';
    private currentModel: string;
    private currentWorkflowKey: string = '';

    private openAiSettings$: Observable<ThirdPartySettings> = this.store.select(currentOpenAiSettings());
    private globalPrompts$: Observable<Prompt[]> = this.store.select(currentPrompts());
    private globalPrompts: Prompt[];

    private shouldAutoScroll: boolean = true;

    constructor(
        private dialog: MatDialog,
        private store: Store,
        private docViewerStore: DocViewerStore,
        private userConfigsService: UserConfigsService,
        private aiAssistantStore: AiAssistantStore
    ) {
        super();
        this.currentUserId = this.userConfigsService?.currentConfig?.userId;

        this.openAiSettings$.pipe(
            tap((settings) => this.currentModel = settings.model),
            takeUntil(this.destroy$)
        ).subscribe();

        this.globalPrompts$.pipe(
            filter((prompts) => !!prompts),
            map((prompts) => {
                this.aiAssistantStore.addPrompts(prompts)
                return prompts
            }),
            tap((prompts) => this.globalPrompts = prompts),
            takeUntil(this.destroy$)
        ).subscribe();

        this.aiAssistantStore.prompts$.pipe(
            map((prompts) => this.localPrompts = prompts),
            takeUntil(this.destroy$)
        ).subscribe();

        this.aiAssistantStore.currentPrompt$.pipe(
            map((current) => this.selectedPrompt = current),
            tap((prompt) => {
                if (prompt) {
                    this.promptControl.setValue(prompt.title);
                    this.promptControl.updateValueAndValidity();
                    this.showPromptControl = false;
                }
                else {
                    this.promptControl.setValue('');
                }
                this.reloadPrompts();
            }),
            takeUntil(this.destroy$)
        ).subscribe();

        this.aiAssistantStore.currentDocId$.pipe(
            map((id: string) => this.currentDocId = id),
            takeUntil(this.destroy$)
        ).subscribe();

        this.aiAssistantStore.hasMoreResponse$.pipe(
            map((v) => this.showContinue = v),
            takeUntil(this.destroy$)
        ).subscribe();

        this.aiAssistantStore.currentWorkflowKey$.pipe(
            map((workflowKey) => {
                this.currentWorkflowKey = workflowKey;
                if (workflowKey)
                    this.aiAssistantStore.scheduleStreaming({ waitInMs: 5000, autoScroll: this.autoScroll.bind(this) });
            }),
            takeUntil(this.destroy$)
        ).subscribe();

        this.status$.pipe(
            map((status) => {
                this.showRetry = false;
                switch (status) {
                    case 'initializing':
                        this.setLoadingStatus(false);
                        break;
                    case 'waiting':
                        this.autoScroll();
                        this.setLoadingStatus(false);
                        break;
                    case 'prompting':
                        this.mode = 'query';
                        this.setLoadingStatus(true);
                        break;
                    case 'responding':
                        this.mode = 'indeterminate';
                        this.shouldAutoScroll = true;
                        this.setLoadingStatus(true);
                        break;
                    case 'continuing':
                        this.setLoadingStatus(true);
                        break;
                    case 'error':
                        this.showRetry = true;
                        this.setLoadingStatus(false);
                        break;
                }
            }),
            takeUntil(this.destroy$)).subscribe();

        this.promptControl.valueChanges.pipe(
            distinctUntilChanged(),
            map((data: string | any) => {
                this.reloadPrompts();
                if (data && typeof (data) === 'string') {
                    if (data.length < 256)
                        this.filterLocalPrompts(data);
                    else
                        this.localPrompts = [];
                    this.showTokenWarning = this.aiAssistantStore.displayTokenLimitWarning(this.currentModel, data);
                    this.showTokenError = this.aiAssistantStore.displayTokenLimitError(this.currentModel, data);
                }
                else if (data && typeof (data) !== 'string') {
                    this.filterLocalPrompts(data.title);
                    this.aiAssistantStore.setPrompt(data);
                    this.showTokenWarning = false;
                    this.showTokenError = false;
                }
                else {
                    this.reloadPrompts();
                    this.aiAssistantStore.setPrompt(null);
                    this.showRetry = false;
                    this.showContinue = false;
                    this.showTokenWarning = false;
                    this.showTokenError = false;
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();

    }
    trackByFn(index: number): number {
        return index;
    }
    ngOnInit(): void {
        this.aiAssistantStore.initialize(AiEngineModels.find(x => x.id === ThirdPartyId.OpenAi)?.value);
        this.docViewerStore.currentDocument$.pipe(
            distinctUntilChanged((a, b) => a.docId === b.docId),
            map((doc) => {
                this.aiAssistantStore.setDocId(doc.docId);
                this.resetAll();

                if (this.selectedPrompt) {
                    this.showPromptControl = false;
                    this.filterLocalPrompts(this.selectedPrompt.title);
                    this.aiAssistantStore.answerPrompt();
                }
                else {
                    this.promptControl.setValue('');
                }
            }),
            takeUntil(this.destroy$),
        ).subscribe();
    }

    public ngOnDestroy(): void {
        this.aiAssistantStore.setPrompt(null);
        if (this.isLoading)
            this.cancelRequest();
        this.aiAssistantStore.setDocId(null);
        super.ngOnDestroy();
    }

    public onScroll(event): void {
        if (Math.abs(event.srcElement?.scrollHeight - event.srcElement?.scrollTop - event.srcElement?.clientHeight) < 2)
            this.shouldAutoScroll = true;
        else
            this.shouldAutoScroll = false;
    }

    public displayFn(prompt: Prompt): string {
        return prompt?.fullPrompt || '';
    }

    public enterPrompt($event: KeyboardEvent | MouseEvent): void {
        $event.preventDefault();
        if (this.showTokenError)
            return;
        const data = this.promptControl.value;
        if (data && typeof (data) === 'string') {
            if (this.promptControl.value.trim().length === 0)
                return;
            this.aiAssistantStore.createAndPrompt(this.promptControl.value);
            return;
        }
        this.showPromptControl = false;
        this.aiAssistantStore.answerPrompt();
    }

    public clearPrompt(): void {
        const panelOpen = this.autocomplete?.panelOpen;
        if (!this.showPromptControl)
            this.showPromptControl = true
        this.promptControl.reset();
        if (panelOpen)
            this.autocomplete.closePanel();
    }

    public cancelRequest(): void {
        this.aiAssistantStore.cancelRequest();
    }

    public togglePromptControl(): void {
        if (!this.isLoading) {
            this.showPromptControl = true;
            if (this.selectedPrompt) {
                this.promptControl.setValue(this.selectedPrompt);
            }
        }
    }

    public copyResponseToClipboard(): void {
        this.aiAssistantStore.copyResponseToClipboard();
    }

    public selectPromptChange(event: MatAutocompleteSelectedEvent): void {
        this.showPromptControl = true;
        this.promptControl.setValue(event.option.value);
    }

    public goToNextChunk(): void {
        this.aiAssistantStore.getNextChunk()
    }

    public retry(): void {
        this.aiAssistantStore.retryPrompt();
    }

    public showClearButton(): boolean {
        const promptValue = this.promptControl.value;
        if (promptValue && typeof (promptValue) === 'string')
            return !this.isLoading && promptValue?.length > 0;
        if (promptValue)
            return !this.isLoading;
        return false;
    }

    public onPanelResizing(): void {
        if (this.autocomplete && this.autocomplete.panelOpen) {
            this.autocomplete.closePanel();
        }
    }

    private resetAll() {
        this.result = [];

        this.showRetry = false;
        this.showContinue = false;
    }

    private reloadPrompts(): void {
        this.aiAssistantStore.clearPrompts();
        this.aiAssistantStore.addPrompts(this.globalPrompts);
    }

    private filterLocalPrompts(term: string = ''): void {
        this.localPrompts = this.localPrompts.filter(p =>
            p.title.toLocaleLowerCase().includes(term.toLocaleLowerCase())
            || p.fullPrompt.toLocaleLowerCase().includes(term.toLocaleLowerCase()));
    }

    private setLoadingStatus(isLoading: boolean) {
        if (isLoading) {
            this.isLoading = true;
            this.promptControl.disable();
        }
        else {
            this.isLoading = false;
            this.promptControl.enable();
        }
    }

    private autoScroll(): void {
        if (this.shouldAutoScroll) {
            try {
                setTimeout(() => {
                    if (this.scrollContainer?.nativeElement)
                        this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight
                });
                // eslint-disable-next-line
            } catch (err) { }
        }
    }

}

